import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Heart, Award, User, UserX, ChevronLeft, ChevronRight, Play } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRESETS = [5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000];
const MODES = [
    { key: 'one_time', label: 'One-time' },
    { key: 'weekly', label: 'Weekly' },
    { key: 'monthly', label: 'Monthly' },
];

const fmt = (n, cur = 'eur') => new Intl.NumberFormat(undefined, { style: 'currency', currency: (cur || 'eur').toUpperCase(), maximumFractionDigits: 0 }).format(Number(n || 0));

// Convert ISO-2 country code to flag emoji
const flagFor = (code) => {
    if (!code || code.length !== 2) return '🌍';
    return String.fromCodePoint(...code.toUpperCase().split('').map(c => 0x1F1E6 - 65 + c.charCodeAt(0)));
};

// Animated progress ring
const ProgressRing = ({ collected, target, currency }) => {
    const [animated, setAnimated] = useState(0);
    const size = 260, stroke = 22, r = (size - stroke) / 2, C = 2 * Math.PI * r;
    const pct = target > 0 ? Math.min(collected / target, 1) : 0;
    useEffect(() => {
        let raf; const t0 = performance.now(); const dur = 1600;
        const tick = (t) => {
            const p = Math.min(1, (t - t0) / dur);
            setAnimated(pct * (1 - Math.pow(1 - p, 3)));  // ease-out
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [pct]);
    return (
        <div className="relative flex items-center justify-center" data-testid="donation-progress-ring">
            <svg width={size} height={size} className="-rotate-90">
                <circle cx={size / 2} cy={size / 2} r={r} stroke="#E5E7EB" strokeWidth={stroke} fill="none" />
                <circle
                    cx={size / 2} cy={size / 2} r={r}
                    stroke="#064E3B" strokeWidth={stroke} fill="none" strokeLinecap="round"
                    strokeDasharray={C}
                    strokeDashoffset={C - C * animated}
                    style={{ transition: 'stroke-dashoffset 60ms linear' }}
                />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-[11px] uppercase tracking-wide text-[#6B7280]">Raised so far</span>
                <span className="text-3xl font-bold text-[#064E3B] font-['Outfit']" data-testid="collected-total">{fmt(collected, currency)}</span>
                <span className="text-xs text-[#4B5563] mt-1">of <span className="font-semibold" data-testid="target-amount">{fmt(target, currency)}</span></span>
                <span className="text-[11px] mt-1 text-[#059669] font-semibold">{Math.round(animated * 100)}%</span>
            </div>
        </div>
    );
};

// Auto-rotating slide carousel
const SlideCarousel = ({ slides }) => {
    const [idx, setIdx] = useState(0);
    const timer = useRef();
    useEffect(() => {
        if (!slides.length) return;
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setIdx(i => (i + 1) % slides.length), 6000);
        return () => clearTimeout(timer.current);
    }, [idx, slides.length]);
    if (!slides.length) {
        return (
            <div className="w-full aspect-video rounded-2xl bg-gradient-to-br from-[#064E3B] via-[#0F766E] to-[#065F46] flex items-center justify-center">
                <div className="text-white text-center px-6">
                    <Heart className="h-10 w-10 mx-auto mb-3 opacity-80" />
                    <h3 className="text-2xl font-bold font-['Outfit']">Support Nurulhuda Quranic School</h3>
                    <p className="mt-2 text-sm opacity-90">Your donation helps our students memorise and understand the Qur'an</p>
                </div>
            </div>
        );
    }
    const s = slides[idx];
    const isYouTube = s.type === 'video' && /(youtube\.com|youtu\.be)/i.test(s.video_url || '');
    const ytId = isYouTube ? (s.video_url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{11})/)?.[1]) : null;
    return (
        <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-black" data-testid="donation-slides">
            {s.type === 'video' ? (
                isYouTube ? (
                    <iframe title={s.title || 'Video'} className="w-full h-full" src={`https://www.youtube.com/embed/${ytId}?autoplay=0&rel=0`} allowFullScreen frameBorder="0" />
                ) : (
                    <video className="w-full h-full object-cover" src={s.video_url} controls />
                )
            ) : (
                <img className="w-full h-full object-cover" src={s.image_url} alt={s.title || 'Slide'} onError={(e) => { e.target.style.opacity = 0.3; }} />
            )}
            {(s.title || s.subtitle) && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-5 text-white">
                    {s.title && <h4 className="text-xl font-bold font-['Outfit']">{s.title}</h4>}
                    {s.subtitle && <p className="text-sm opacity-90 mt-1">{s.subtitle}</p>}
                </div>
            )}
            {slides.length > 1 && (
                <>
                    <button aria-label="Previous slide" className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center" onClick={() => setIdx((idx - 1 + slides.length) % slides.length)}>
                        <ChevronLeft className="h-4 w-4 text-[#064E3B]" />
                    </button>
                    <button aria-label="Next slide" className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center" onClick={() => setIdx((idx + 1) % slides.length)}>
                        <ChevronRight className="h-4 w-4 text-[#064E3B]" />
                    </button>
                    <div className="absolute bottom-3 right-3 flex gap-1.5">
                        {slides.map((_, i) => (
                            <button key={i} onClick={() => setIdx(i)} className={`h-1.5 rounded-full transition-all ${i === idx ? 'bg-white w-6' : 'bg-white/50 w-1.5'}`} aria-label={`Go to slide ${i + 1}`} />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

const Donate = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const initialSuccess = searchParams.get('session_id');
    const [amount, setAmount] = useState(50);
    const [customAmount, setCustomAmount] = useState('');
    const [mode, setMode] = useState('one_time');
    const [donorName, setDonorName] = useState('');
    const [donorEmail, setDonorEmail] = useState('');
    const [inHonourOf, setInHonourOf] = useState('');
    const [anonymous, setAnonymous] = useState(false);
    const [certificate, setCertificate] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const [summary, setSummary] = useState({ total_collected: 0, target_amount: 50000, donor_count: 0, currency: 'eur' });
    const [slides, setSlides] = useState([]);
    const [feed, setFeed] = useState({ total: 0, page: 1, size: 20, results: [] });
    const [feedPage, setFeedPage] = useState(1);
    const feedRef = useRef();

    useEffect(() => {
        axios.get(`${API_URL}/api/public/donations/summary`).then(r => setSummary(r.data)).catch(() => {});
        axios.get(`${API_URL}/api/public/donations/slides`).then(r => setSlides(r.data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        axios.get(`${API_URL}/api/public/donations/feed?page=${feedPage}&size=20`).then(r => setFeed(r.data)).catch(() => {});
    }, [feedPage]);

    // If we landed here with ?session_id=..., forward to /donate/success
    useEffect(() => {
        if (initialSuccess) navigate(`/donate/success?session_id=${initialSuccess}`, { replace: true });
    }, [initialSuccess, navigate]);

    const pickAmount = (v) => { setAmount(v); setCustomAmount(''); };
    const pickCustom = (v) => {
        setCustomAmount(v);
        const n = parseFloat(v);
        if (!isNaN(n) && n > 0) setAmount(n);
    };

    const submit = async (e) => {
        e.preventDefault(); setError('');
        const finalAmount = customAmount ? parseFloat(customAmount) : amount;
        if (!finalAmount || finalAmount < 1) return setError('Please choose an amount of at least €1');
        if (!donorEmail) return setError('Email is required so we can send you a receipt');
        if (!anonymous && !donorName.trim()) return setError('Please provide your name, or tick "Donate anonymously"');
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/public/donations/checkout`, {
                amount: finalAmount,
                mode,
                donor_name: donorName.trim(),
                donor_email: donorEmail.trim(),
                anonymous,
                certificate,
                in_honour_of: inHonourOf.trim(),
                origin_url: window.location.origin,
            });
            window.location.href = data.stripe_url;
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not start payment. Please try again.');
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#FAF9F6] to-[#F3F4F6]" data-testid="donate-page">
            {/* Hero */}
            <header className="bg-[#064E3B] text-white">
                <div className="max-w-6xl mx-auto px-4 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div>
                        <p className="text-xs uppercase tracking-widest opacity-70">Nurulhuda Quranic School</p>
                        <h1 className="text-4xl sm:text-5xl font-bold font-['Outfit'] mt-1">Donate & Give with Love</h1>
                        <p className="mt-3 text-base sm:text-lg max-w-xl opacity-90">Support the memorisation and teaching of the Qur'an. Every euro fuels our classes, our students, and their teachers.</p>
                    </div>
                    <ProgressRing collected={summary.total_collected} target={summary.target_amount} currency={summary.currency} />
                </div>
            </header>

            {/* Main */}
            <main className="max-w-6xl mx-auto px-4 py-10 grid grid-cols-1 lg:grid-cols-5 gap-8">
                {/* Left: slides + feed */}
                <section className="lg:col-span-3 space-y-6">
                    <SlideCarousel slides={slides} />

                    <div className="bg-white rounded-2xl shadow-sm p-5 border border-[#E5E7EB]" data-testid="donation-feed">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-[#064E3B] font-['Outfit'] flex items-center gap-2"><Heart className="h-5 w-5 text-[#DC2626]" />Recent Donations</h3>
                            <span className="text-xs text-[#6B7280]">{feed.total} donors</span>
                        </div>
                        {feed.results.length === 0 ? (
                            <p className="text-sm text-[#6B7280] italic py-6 text-center">Be the first to donate.</p>
                        ) : (
                            <ul className="divide-y divide-[#E5E7EB]">
                                {feed.results.map((d, i) => (
                                    <li key={i} className="py-2.5 flex items-center gap-3 text-sm" data-testid={`donation-row-${i}`}>
                                        <span className="text-2xl" title={d.country_code || ''}>{flagFor(d.country_code)}</span>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {d.anonymous ? (
                                                    <span className="font-medium text-[#6B7280] flex items-center gap-1"><UserX className="h-3.5 w-3.5" />Anonymous</span>
                                                ) : (
                                                    <span className="font-medium text-[#1F2937]">{d.display_name}</span>
                                                )}
                                                {d.interval && (
                                                    <span className="text-[10px] uppercase tracking-wide bg-[#EDE9FE] text-[#5B21B6] px-1.5 py-0.5 rounded">{d.interval}ly</span>
                                                )}
                                                {d.in_honour_of && !d.anonymous && (
                                                    <span className="text-xs italic text-[#4B5563]">in honour of {d.in_honour_of}</span>
                                                )}
                                            </div>
                                            <div className="text-[11px] text-[#6B7280]">{new Date(d.created_at).toLocaleString()}</div>
                                        </div>
                                        <span className="font-semibold text-[#064E3B]">{fmt(d.amount, d.currency)}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {feed.total > feed.size && (
                            <div className="flex justify-center gap-2 mt-4">
                                <button className="text-xs px-3 py-1.5 rounded border border-[#E5E7EB] hover:bg-[#F9FAFB] disabled:opacity-40" disabled={feedPage <= 1} onClick={() => setFeedPage(p => p - 1)} data-testid="feed-prev">Prev</button>
                                <span className="text-xs text-[#6B7280] self-center">Page {feed.page} of {Math.ceil(feed.total / feed.size) || 1}</span>
                                <button className="text-xs px-3 py-1.5 rounded border border-[#E5E7EB] hover:bg-[#F9FAFB] disabled:opacity-40" disabled={feedPage * feed.size >= feed.total} onClick={() => setFeedPage(p => p + 1)} data-testid="feed-next">Next</button>
                            </div>
                        )}
                    </div>
                </section>

                {/* Right: donation form */}
                <section className="lg:col-span-2">
                    <form onSubmit={submit} className="bg-white rounded-2xl shadow-md p-6 border border-[#E5E7EB] space-y-5 sticky top-6" data-testid="donation-form">
                        <h2 className="text-xl font-bold text-[#064E3B] font-['Outfit']">Make a Donation</h2>

                        {/* Mode toggle */}
                        <div className="grid grid-cols-3 gap-1 bg-[#F3F4F6] rounded-lg p-1">
                            {MODES.map(m => (
                                <button
                                    key={m.key} type="button" onClick={() => setMode(m.key)}
                                    className={`py-2 rounded-md text-xs font-semibold transition-all ${mode === m.key ? 'bg-[#064E3B] text-white shadow' : 'text-[#4B5563] hover:bg-white'}`}
                                    data-testid={`mode-${m.key}`}
                                >{m.label}</button>
                            ))}
                        </div>

                        {/* Amount presets */}
                        <div>
                            <label className="text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Amount ({(summary.currency || 'eur').toUpperCase()})</label>
                            <div className="grid grid-cols-5 gap-2 mt-2">
                                {PRESETS.map(p => (
                                    <button key={p} type="button" onClick={() => pickAmount(p)}
                                        className={`py-2 rounded-lg text-sm font-semibold border transition ${amount === p && !customAmount ? 'bg-[#064E3B] text-white border-[#064E3B]' : 'bg-white text-[#1F2937] border-[#E5E7EB] hover:border-[#064E3B]'}`}
                                        data-testid={`preset-${p}`}>€{p}</button>
                                ))}
                            </div>
                            <input
                                type="number" min="1" step="1" placeholder="Or enter a custom amount"
                                value={customAmount} onChange={(e) => pickCustom(e.target.value)}
                                className="mt-2 w-full px-3 py-2 rounded-lg border border-[#E5E7EB] focus:border-[#064E3B] focus:ring-1 focus:ring-[#064E3B] outline-none text-sm"
                                data-testid="custom-amount"
                            />
                        </div>

                        {/* Donor identity */}
                        <div className="space-y-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={anonymous} onChange={e => setAnonymous(e.target.checked)} data-testid="anonymous-toggle" />
                                <span className="text-sm text-[#1F2937]">Donate anonymously (hide my name in the feed)</span>
                            </label>
                            {!anonymous && (
                                <input placeholder="Your name (will appear in the feed)" value={donorName} onChange={e => setDonorName(e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg border border-[#E5E7EB] focus:border-[#064E3B] focus:ring-1 focus:ring-[#064E3B] outline-none text-sm" data-testid="donor-name" />
                            )}
                            <input type="email" required placeholder="Email (for your receipt)" value={donorEmail} onChange={e => setDonorEmail(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border border-[#E5E7EB] focus:border-[#064E3B] focus:ring-1 focus:ring-[#064E3B] outline-none text-sm" data-testid="donor-email" />
                            <input placeholder="In honour of (optional, e.g. 'my parents')" value={inHonourOf} onChange={e => setInHonourOf(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border border-[#E5E7EB] focus:border-[#064E3B] focus:ring-1 focus:ring-[#064E3B] outline-none text-sm" data-testid="in-honour-of" />
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={certificate} onChange={e => setCertificate(e.target.checked)} data-testid="certificate-toggle" />
                                <span className="text-sm text-[#1F2937] flex items-center gap-1"><Award className="h-4 w-4 text-[#B45309]" />Email me a PDF donation certificate</span>
                            </label>
                        </div>

                        {error && <p className="text-sm text-[#DC2626] font-medium" data-testid="donate-error">{error}</p>}

                        <button type="submit" disabled={submitting}
                            className="w-full py-3 bg-[#064E3B] hover:bg-[#065F46] disabled:opacity-60 text-white rounded-lg font-semibold flex items-center justify-center gap-2 shadow-md"
                            data-testid="donate-submit">
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className="h-4 w-4" />}
                            {mode === 'one_time' ? `Donate ${fmt(customAmount || amount, summary.currency)}` : `Give ${fmt(customAmount || amount, summary.currency)} / ${mode === 'weekly' ? 'week' : 'month'}`}
                        </button>
                        <p className="text-[11px] text-[#6B7280] text-center">Secure payment powered by Stripe · Your card details never touch our servers.</p>
                    </form>
                </section>
            </main>

            <footer className="border-t border-[#E5E7EB] bg-white">
                <div className="max-w-6xl mx-auto px-4 py-6 text-center text-xs text-[#6B7280]">
                    Nurulhuda Quranic School · info@nhq.ie
                </div>
            </footer>
        </div>
    );
};

export default Donate;
